import { useState } from "react";
import { PERSONAS, ALL_DEPTS } from "../data/org";
import { useRH } from "../store/rh";
import { useSettings } from "../store/settings";
import { profileOf } from "../data/profiles";
import { generateCafeteriaJokesAI } from "../lib/claude";
import { initials, monogramColor } from "../lib/monogram";

const COMEDIANTES = ["chespirito", "chaves", "ceara", "paulinhogogo"];

export function Cafeteria({ onClose }: { onClose: () => void }) {
  const employees = useRH(s => s.employees);
  const settings  = useSettings();
  const [script, setScript]   = useState("");
  const [loading, setLoading] = useState(false);

  // alvos = contratados que NÃO são os 4 comediantes
  const victims = PERSONAS
    .filter(p => employees[p.id]?.status === "hired")
    .filter(p => !COMEDIANTES.includes(p.id));

  // comediantes contratados (precisam estar contratados pra sessão acontecer)
  const onstage = PERSONAS.filter(p => COMEDIANTES.includes(p.id) && employees[p.id]?.status === "hired");

  const generate = async () => {
    if (!settings.anthropicKey) { alert("Configure chave Anthropic em ⚙️."); return; }
    if (onstage.length === 0) {
      alert("Nenhum comediante no Endomarketing foi contratado. Contrate Chespirito, Chaves, Matheus Ceará ou Paulinho Gogó.");
      return;
    }
    if (victims.length === 0) {
      alert("Não tem ninguém pra zoar. Contrate algumas vítimas (digo, colegas) antes.");
      return;
    }
    setLoading(true);
    try {
      const list = victims.map(v => {
        const prof = profileOf(v.id, v.tier);
        const [dId] = v.slotId.split(":");
        const dept = ALL_DEPTS.find(d => d.id === dId)?.name ?? dId;
        return { name: v.name, bio: v.bio, dept, voice: prof.voice };
      });
      const text = await generateCafeteriaJokesAI({
        apiKey: settings.anthropicKey,
        model:  settings.model,
        victims: list
      });
      setScript(text);
    } catch (e) {
      alert("Falha: " + (e as Error).message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-ink/80 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-bone border-2 border-ink w-full max-w-4xl max-h-[92vh] overflow-y-auto
                      shadow-[6px_6px_0_0_#121214]"
           onClick={(e) => e.stopPropagation()}>
        <header className="sticky top-0 z-10 bg-gold text-ink px-5 py-4 flex items-center justify-between">
          <div>
            <div className="font-mono text-xs">CAFE-01</div>
            <h2 className="font-display font-black uppercase text-3xl leading-none">☕ HORA DA CAFETERIA</h2>
            <div className="font-serif italic mt-1">
              sessão de piadas comandada pelo Endomarketing · {victims.length} alvo{victims.length === 1 ? "" : "s"} no salão
            </div>
          </div>
          <button onClick={onClose} className="btn bg-ink text-bone border-ink">fechar ×</button>
        </header>

        <div className="p-5 space-y-4">
          {/* PALCO */}
          <section className="border-2 border-ink p-4 bg-bone">
            <div className="font-display font-bold uppercase text-base mb-3">no palco improvisado</div>
            {onstage.length === 0 ? (
              <p className="font-crimson italic text-paper text-sm">
                Sem comediante. Contrate ao menos um: Chespirito, Chaves, Matheus Ceará ou Paulinho Gogó.
              </p>
            ) : (
              <div className="flex gap-3 flex-wrap">
                {onstage.map(p => (
                  <div key={p.id} className="border-2 border-ink bg-gold/20 px-3 py-2 flex items-center gap-2">
                    <div className="w-9 h-9 flex items-center justify-center font-display font-black text-sm"
                         style={{ background: monogramColor(p.id), color: "#F2EEE6" }}>
                      {initials(p.name)}
                    </div>
                    <div>
                      <div className="font-display font-bold uppercase text-sm">{p.name}</div>
                      <div className="font-crimson italic text-[11px] text-paper">comediante residente</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>

          {/* ALVOS */}
          <section className="border-2 border-ink p-4 bg-bone">
            <div className="font-display font-bold uppercase text-base mb-3">público (vítimas amigáveis)</div>
            {victims.length === 0 ? (
              <p className="font-crimson italic text-paper text-sm">Ninguém na cafeteria. Contrate funcionários primeiro.</p>
            ) : (
              <div className="grid grid-cols-3 md:grid-cols-6 gap-2">
                {victims.map(p => (
                  <div key={p.id} className="flex flex-col items-center" title={p.name}>
                    <div className="w-10 h-10 flex items-center justify-center font-display font-black text-xs"
                         style={{ background: monogramColor(p.id), color: "#F2EEE6" }}>
                      {initials(p.name)}
                    </div>
                    <div className="font-mono text-[9px] text-center text-graydk mt-1 truncate w-full">
                      {p.name}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>

          {/* BOTÃO */}
          <div className="flex justify-center">
            <button onClick={generate} disabled={loading}
                    className="btn bg-red text-bone border-red text-lg px-6 py-3 disabled:opacity-50">
              {loading ? "🎤 esquentando o microfone…" : "🎤 começar sessão de piadas"}
            </button>
          </div>

          {/* SCRIPT */}
          {script && (
            <section className="border-2 border-ink bg-bone">
              <header className="bg-ink text-bone px-4 py-2 flex items-center justify-between">
                <span className="font-display font-bold uppercase">Transcrição do happy hour</span>
                <div className="flex gap-2">
                  <button onClick={() => navigator.clipboard.writeText(script)} className="tag underline">copiar</button>
                  <button onClick={() => setScript("")} className="tag underline">limpar</button>
                </div>
              </header>
              <pre className="p-5 font-mono text-[13px] whitespace-pre-wrap leading-relaxed">
{script}
              </pre>
            </section>
          )}

          <p className="text-center font-crimson italic text-graydk text-xs">
            sessão fictícia gerada por IA · zoeira amigável de happy hour, não usar fora do contexto
          </p>
        </div>
      </div>
    </div>
  );
}
